import {GitHubAvatar} from '@github-ui/github-avatar'
import {
  EyeIcon,
  type Icon,
  KeyIcon,
  PencilIcon,
  PeopleIcon,
  PersonIcon,
  RepoPushIcon,
  ToolsIcon,
} from '@primer/octicons-react'
import type {FC} from 'react'

import styles from './ActorAvatar.module.css'
import {humanizeRoleName} from './helpers/humanize-role-name'
import type {Actor} from './types'

/**
 * Role icons keyed by the *humanized* role label (so `admin` →
 * `Repository admin` → `EyeIcon`). The lookup is case-insensitive to
 * accommodate either the raw role slug or the humanized form.
 */
const roleIconMap: Record<string, Icon> = {
  write: PencilIcon,
  maintain: ToolsIcon,
  admin: EyeIcon,
  'repository admin': EyeIcon,
  'organization admin': EyeIcon,
}

/** Type-keyed icons for actor types that don't have an avatar URL. */
const typeIconMap: Record<string, Icon> = {
  DeployKey: KeyIcon,
  BusinessTeam: PeopleIcon,
  EnterpriseOwner: PeopleIcon,
  RepositoryMigration: RepoPushIcon,
  EnterpriseRole: PeopleIcon,
  OrganizationRole: PeopleIcon,
}

/** Actor types that resolve to a `<GitHubAvatar>` URL when one is derivable. */
const URL_PREFIX_BY_TYPE: Record<string, string> = {
  Team: 't',
  Integration: 'in',
  User: 'u',
}

export interface ActorAvatarProps {
  actor: Actor
  /**
   * Base URL used to construct avatar image URLs (e.g. `${baseUrl}/t/${id}`)
   * when a per-actor `preferredAvatarUrl` isn't supplied.
   */
  baseUrl?: string
  size?: number
}

/**
 * Generic avatar for any `BaseActor`. Resolves an image source in the
 * following priority order, then falls back to a role/type octicon:
 *
 * 1. `actor.preferredAvatarUrl` (already-resolved server URL)
 * 2. `${baseUrl}/{prefix}/{actor.actorId}` for `Team` / `Integration` /
 *    `User` actors when both `baseUrl` and `actor.actorId` are set
 * 3. Role-based octicon for `RepositoryRole` / `OrganizationAdmin`
 * 4. Type-based octicon (`DeployKey`, `BusinessTeam`, …)
 * 5. `<PersonIcon>` for unknown shapes when no other resolution applies
 */
export const ActorAvatar: FC<ActorAvatarProps> = ({actor, baseUrl, size = 16}) => {
  const displayName = humanizeRoleName(actor)

  if (actor.preferredAvatarUrl) {
    return <GitHubAvatar src={actor.preferredAvatarUrl} alt={displayName} size={size} />
  }

  const urlPrefix = URL_PREFIX_BY_TYPE[actor.actorType]
  if (urlPrefix && baseUrl && actor.actorId != null) {
    return <GitHubAvatar src={`${baseUrl}/${urlPrefix}/${actor.actorId}`} alt={displayName} size={size} />
  }

  if (actor.actorType === 'RepositoryRole' || actor.actorType === 'OrganizationAdmin') {
    const RoleIcon = roleIconMap[displayName.toLowerCase()] ?? roleIconMap[actor.name.toLowerCase()] ?? PersonIcon
    return <RoleIcon className={styles.Octicon} />
  }

  const TypeIcon = typeIconMap[actor.actorType] ?? PersonIcon
  return <TypeIcon className={styles.Octicon} />
}
